import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLicenseStatus } from '../../hooks/useLicenseStatus';
import LoadingSpinner from './LoadingSpinner';
import { FiClock, FiXCircle, FiKey, FiCopy, FiCheck, FiPauseCircle } from 'react-icons/fi';
import { apiClient } from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';
import { generateHardDiskId, generateStableDeviceId, getMachineName } from '../../utils/hardwareId';
import { collectDeviceInfo } from '../../services/deviceRegistration';
import { deviceCodeFromHardDiskId, formatDeviceCode } from '../../utils/licenseActivation';
import { writeLicenseValidationCache, clearLicenseValidationCache } from '../../utils/licenseValidationCache';
import toast from 'react-hot-toast';

interface LicenseStartupGateProps {
  children: ReactNode;
  enabled?: boolean;
}

export default function LicenseStartupGate({ children, enabled = true }: LicenseStartupGateProps) {
  const queryClient = useQueryClient();
  const {
    canAccess,
    isLoading,
    isPending,
    isRejected,
    isSuspended,
    isDeleted,
    isExpired,
    hasNoLicense,
    licenseStatus,
  } = useLicenseStatus(enabled);
  const [activationCode, setActivationCode] = useState('');
  const [activating, setActivating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  const deviceCode = useMemo(() => {
    try {
      return formatDeviceCode(deviceCodeFromHardDiskId(generateHardDiskId()));
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (!enabled || !isLoading) return;
    const timer = window.setTimeout(() => setWaitedTooLong(true), 2500);
    return () => window.clearTimeout(timer);
  }, [enabled, isLoading]);

  if (!enabled) {
    return <>{children}</>;
  }

  if (canAccess) {
    return <>{children}</>;
  }

  if (isLoading && !waitedTooLong) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-base-200">
        <LoadingSpinner />
        <p className="text-base-content/70">جاري التحقق من الترخيص...</p>
      </div>
    );
  }

  const deviceLabel =
    licenseStatus?.licenseName ||
    licenseStatus?.companyName ||
    'جهاز غير مرخّص';

  let title = 'البرنامج غير مرخّص';
  let message = 'انسخ رقم الجهاز وأرسله للمسؤول. بعد توليد كود التفعيل من Signal Management ضعه هنا.';
  let Icon = FiKey;
  let iconClass = 'text-warning';

  if (isPending) {
    title = 'البرنامج غير مرخّص';
    message = 'في انتظار الموافقة، أو أدخل كود التفعيل الذي يولّده Signal Management لمدة أيام محددة.';
    Icon = FiClock;
    iconClass = 'text-info';
  } else if (isSuspended) {
    title = 'تم إيقاف البرنامج مؤقتاً';
    message = 'أوقف المسؤول التشغيل من Signal Management. سيُستأنف تلقائياً عند رفع الإيقاف، أو أدخل كود تفعيل جديد.';
    Icon = FiPauseCircle;
    iconClass = 'text-warning';
  } else if (isDeleted) {
    title = 'تم حذف الترخيص';
    message = 'حُذف الترخيص من الإدارة. أرسل رقم الجهاز للمسؤول وضع كود تفعيل جديد هنا.';
    Icon = FiXCircle;
    iconClass = 'text-error';
  } else if (isRejected) {
    title = 'تم رفض الترخيص';
    message = 'يمكنك إدخال كود تفعيل جديد من Signal Management لتشغيل البرنامج.';
    Icon = FiXCircle;
    iconClass = 'text-error';
  } else if (isExpired) {
    title = 'انتهى الترخيص';
    message = 'انتهت مدة الترخيص. أرسل رقم الجهاز للمسؤول وضع كود التفعيل الجديد هنا.';
    Icon = FiXCircle;
    iconClass = 'text-error';
  } else if (hasNoLicense) {
    title = 'البرنامج غير مرخّص';
    Icon = FiKey;
    iconClass = 'text-warning';
  }

  async function copyDeviceCode() {
    if (!deviceCode) return;
    try {
      await navigator.clipboard.writeText(deviceCode);
      setCopied(true);
      toast.success('تم نسخ رقم الجهاز');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('تعذر النسخ');
    }
  }

  async function activate() {
    const code = activationCode.trim();
    if (!code) {
      toast.error('أدخل كود التفعيل');
      return;
    }

    setActivating(true);
    try {
      const hardDiskId = generateHardDiskId();
      const deviceInfo = collectDeviceInfo();
      const response = await apiClient.post(API_ENDPOINTS.license.activateCode, {
        activationCode: code,
        hardDiskId,
        deviceId: generateStableDeviceId(),
        deviceName: deviceInfo.deviceName,
        machineName: getMachineName(),
      });
      toast.success(response.data?.message || 'تم تفعيل الترخيص');
      setActivationCode('');
      clearLicenseValidationCache();
      writeLicenseValidationCache({
        hardDiskId,
        status: response.data?.approvalStatus || 'Approved',
        isValid: response.data?.isValid === true,
        remainingDays: response.data?.remainingDays,
        expiryDate: response.data?.expiryDate,
        daysCount: response.data?.daysCount,
        startDate: response.data?.startDate,
        companyName: response.data?.companyName,
        licenseName: response.data?.licenseName,
        approvalStatus: response.data?.approvalStatus || 'Approved',
        message: response.data?.message,
      });
      await queryClient.invalidateQueries({ queryKey: ['licenseStatus'] });
      await queryClient.invalidateQueries({ queryKey: ['activeLicense'] });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'فشل تفعيل الترخيص');
    } finally {
      setActivating(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-6">
      <div className="max-w-lg w-full bg-base-100 rounded-2xl shadow-xl border border-base-300 p-8 text-center">
        <Icon className={`mx-auto text-5xl mb-4 ${iconClass}`} />
        <h1 className="text-2xl font-bold mb-3">{title}</h1>
        <p className="text-base-content/80 mb-6 leading-relaxed">{message}</p>

        <div className="rounded-xl bg-base-200 px-4 py-4 mb-4">
          <div className="text-xs uppercase tracking-wide text-base-content/50 mb-1">رقم الجهاز</div>
          <div className="flex items-center justify-center gap-2" dir="ltr">
            <span className="font-mono text-2xl font-bold tracking-widest select-all">
              {deviceCode || '........'}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void copyDeviceCode()}
              title="نسخ"
            >
              {copied ? <FiCheck /> : <FiCopy />}
            </button>
          </div>
          <div className="mt-2 text-xs text-base-content/50">{deviceLabel}</div>
        </div>

        <label className="block text-right text-sm font-semibold mb-2">كود التفعيل</label>
        <input
          className="input input-bordered w-full font-mono text-center tracking-wide mb-3"
          dir="ltr"
          value={activationCode}
          onChange={(e) => setActivationCode(e.target.value)}
          placeholder="SA1-xxxxxxxxxxxx-0365-YYYYMMDD-XXXXXXXXXX"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void activate();
          }}
        />
        <button
          type="button"
          className="btn btn-primary w-full"
          disabled={activating || !activationCode.trim()}
          onClick={() => void activate()}
        >
          {activating ? 'جاري التفعيل...' : 'تفعيل الترخيص'}
        </button>

        {isPending && (
          <p className="mt-4 text-sm text-base-content/60">
            يتم التحقق تلقائياً كل بضع ثوانٍ...
          </p>
        )}
      </div>
    </div>
  );
}
